import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ChannelType, type AutomodRule, type AutomodTrigger } from '@comms/shared';
import { useStore } from '../../store';
import { api } from '../../lib/api';
import { Button, ErrorText, Input, Spinner, cx } from '../ui';

type TriggerType = AutomodTrigger['type'];
const TRIGGERS: { type: TriggerType; label: string; desc: string }[] = [
  {
    type: 'keyword',
    label: 'Blocked words',
    desc: 'Words or phrases. word = whole word, *word* = anywhere, word* / *word = starts/ends with.',
  },
  { type: 'links', label: 'Links', desc: 'Links to any site except the ones you allow.' },
  { type: 'mention_spam', label: 'Mention spam', desc: 'Too many @mentions in one message.' },
];

const lines = (s: string) =>
  s
    .split(/[\n,]/)
    .map((x) => x.trim())
    .filter(Boolean);

export function AutomodSettings({ guildId }: { guildId: string }) {
  const [rules, setRules] = useState<AutomodRule[] | null>(null);
  const [editing, setEditing] = useState<AutomodRule | 'new' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const load = () =>
    api<AutomodRule[]>('GET', `/guilds/${guildId}/automod/rules`).then(setRules, setError);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guildId]);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-text-0">AutoMod</h2>
      <p className="text-sm text-text-3">
        Rules are checked before a message is posted (and when it's edited). People who can manage
        the server are never affected.
      </p>
      <ErrorText error={error} />
      {!rules && <Spinner />}
      {rules?.map((r) =>
        editing !== 'new' && editing?.id === r.id ? (
          <RuleEditor
            key={r.id}
            guildId={guildId}
            rule={r}
            onDone={() => (setEditing(null), void load())}
          />
        ) : (
          <div key={r.id} className="flex items-center gap-3 rounded bg-bg-1 p-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                aria-label={`Enable ${r.name}`}
                checked={r.enabled}
                onChange={(e) =>
                  void api('PATCH', `/guilds/${guildId}/automod/rules/${r.id}`, {
                    enabled: e.target.checked,
                  }).then(load, setError)
                }
              />
            </label>
            <div className="flex-1">
              <div className="font-medium text-text-0">{r.name}</div>
              <div className="text-xs text-text-3">
                {TRIGGERS.find((t) => t.type === r.trigger.type)?.label} ·{' '}
                {[
                  r.actions.block && 'block',
                  r.actions.alertChannelId && 'alert',
                  r.actions.timeoutSeconds &&
                    `timeout ${Math.round(r.actions.timeoutSeconds / 60)}m`,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            </div>
            <Button variant="ghost" onClick={() => setEditing(r)}>
              Edit
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                confirm(`Delete the rule "${r.name}"?`) &&
                void api('DELETE', `/guilds/${guildId}/automod/rules/${r.id}`).then(load, setError)
              }
            >
              Delete
            </Button>
          </div>
        ),
      )}
      {editing === 'new' ? (
        <RuleEditor guildId={guildId} onDone={() => (setEditing(null), void load())} />
      ) : (
        rules && rules.length < 10 && <Button onClick={() => setEditing('new')}>Create rule</Button>
      )}
    </div>
  );
}

function RuleEditor({
  guildId,
  rule,
  onDone,
}: {
  guildId: string;
  rule?: AutomodRule;
  onDone: () => void;
}) {
  const textChannels = useStore(
    useShallow((s) =>
      Object.values(s.channels).filter(
        (c) => c.guildId === guildId && c.type === ChannelType.GUILD_TEXT,
      ),
    ),
  );
  const allChannels = useStore(
    useShallow((s) =>
      Object.values(s.channels).filter((c) => c.guildId === guildId && !c.threadMetadata),
    ),
  );
  const roles = useStore(
    useShallow((s) => (s.guilds[guildId]?.roles ?? []).filter((r) => r.id !== guildId)),
  );
  const t = rule?.trigger;
  const [name, setName] = useState(rule?.name ?? '');
  const [type, setType] = useState<TriggerType>(t?.type ?? 'keyword');
  const [keywords, setKeywords] = useState(t?.type === 'keyword' ? t.keywords.join('\n') : '');
  const [allowList, setAllowList] = useState(t?.type === 'keyword' ? t.allowList.join('\n') : '');
  const [domains, setDomains] = useState(t?.type === 'links' ? t.allowedDomains.join('\n') : '');
  const [limit, setLimit] = useState(t?.type === 'mention_spam' ? t.mentionLimit : 5);
  const [block, setBlock] = useState(rule ? !!rule.actions.block : true);
  const [blockMessage, setBlockMessage] = useState(rule?.actions.block?.message ?? '');
  const [alertChannelId, setAlert] = useState(rule?.actions.alertChannelId ?? '');
  const [timeoutMin, setTimeoutMin] = useState(
    rule?.actions.timeoutSeconds ? Math.round(rule.actions.timeoutSeconds / 60) : 0,
  );
  const [exemptRoles, setExemptRoles] = useState<string[]>(rule?.exemptRoles ?? []);
  const [exemptChannels, setExemptChannels] = useState<string[]>(rule?.exemptChannels ?? []);
  const [error, setError] = useState<unknown>(null);

  async function save() {
    const trigger: AutomodTrigger =
      type === 'keyword'
        ? { type, keywords: lines(keywords), allowList: lines(allowList) }
        : type === 'links'
          ? { type, allowedDomains: lines(domains) }
          : { type, mentionLimit: limit };
    const body = {
      name,
      trigger,
      actions: {
        block: block ? { message: blockMessage.trim() || null } : null,
        alertChannelId: alertChannelId || null,
        timeoutSeconds: timeoutMin > 0 ? timeoutMin * 60 : null,
      },
      exemptRoles,
      exemptChannels,
    };
    try {
      if (rule) await api('PATCH', `/guilds/${guildId}/automod/rules/${rule.id}`, body);
      else await api('POST', `/guilds/${guildId}/automod/rules`, body);
      onDone();
    } catch (err) {
      setError(err);
    }
  }

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  const area = 'w-full rounded bg-bg-0 px-3 py-2 text-sm text-text-0 outline-none';

  return (
    <div className="space-y-3 rounded-lg border border-bg-3 bg-bg-1 p-4">
      <Input label="Rule name" value={name} onChange={(e) => setName(e.target.value)} />
      <div className="grid grid-cols-3 gap-2">
        {TRIGGERS.map((o) => (
          <label
            key={o.type}
            className={cx(
              'cursor-pointer rounded p-2 text-sm',
              type === o.type ? 'bg-bg-active text-text-0' : 'bg-bg-2 text-text-2',
            )}
          >
            <input
              type="radio"
              className="mr-1"
              checked={type === o.type}
              onChange={() => setType(o.type)}
            />
            {o.label}
            <span className="mt-1 block text-xs text-text-3">{o.desc}</span>
          </label>
        ))}
      </div>
      {type === 'keyword' && (
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-text-2">
            Blocked words (one per line)
            <textarea
              rows={4}
              className={area}
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
            />
          </label>
          <label className="text-sm text-text-2">
            Always allowed
            <textarea
              rows={4}
              className={area}
              value={allowList}
              onChange={(e) => setAllowList(e.target.value)}
            />
          </label>
        </div>
      )}
      {type === 'links' && (
        <label className="block text-sm text-text-2">
          Allowed domains (one per line, subdomains included)
          <textarea
            rows={3}
            className={area}
            value={domains}
            placeholder={'youtube.com\ngithub.com'}
            onChange={(e) => setDomains(e.target.value)}
          />
        </label>
      )}
      {type === 'mention_spam' && (
        <Input
          label="Most mentions allowed per message"
          type="number"
          min={1}
          max={50}
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
        />
      )}

      <div className="space-y-2 rounded bg-bg-2 p-3 text-sm text-text-1">
        <div className="text-xs font-bold uppercase text-text-2">When a message matches</div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={block} onChange={(e) => setBlock(e.target.checked)} />
          Block it, telling the sender:
          <input
            className="flex-1 rounded bg-bg-0 px-2 py-1"
            placeholder="This message was blocked by the server's AutoMod."
            maxLength={150}
            disabled={!block}
            value={blockMessage}
            onChange={(e) => setBlockMessage(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-2">
          Send an alert to
          <select
            value={alertChannelId}
            onChange={(e) => setAlert(e.target.value)}
            className="rounded bg-bg-0 px-2 py-1"
          >
            <option value="">(no alert)</option>
            {textChannels.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          Time the sender out for
          <select
            value={timeoutMin}
            onChange={(e) => setTimeoutMin(Number(e.target.value))}
            className="rounded bg-bg-0 px-2 py-1"
          >
            {[0, 1, 5, 10, 60, 1440, 10080].map((m) => (
              <option key={m} value={m}>
                {m === 0
                  ? '(no timeout)'
                  : m < 60
                    ? `${m} min`
                    : m < 1440
                      ? `${m / 60} h`
                      : `${m / 1440} d`}
              </option>
            ))}
          </select>
        </label>
      </div>

      <details className="text-sm text-text-2">
        <summary className="cursor-pointer">
          Exemptions ({exemptRoles.length + exemptChannels.length})
        </summary>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <div>
            <div className="mb-1 text-xs font-bold uppercase">Roles</div>
            {roles.map((r) => (
              <label key={r.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={exemptRoles.includes(r.id)}
                  onChange={() => setExemptRoles(toggle(exemptRoles, r.id))}
                />
                {r.name}
              </label>
            ))}
          </div>
          <div>
            <div className="mb-1 text-xs font-bold uppercase">Channels and categories</div>
            {allChannels.map((c) => (
              <label key={c.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={exemptChannels.includes(c.id)}
                  onChange={() => setExemptChannels(toggle(exemptChannels, c.id))}
                />
                {c.type === ChannelType.GUILD_CATEGORY ? c.name : `#${c.name}`}
              </label>
            ))}
          </div>
        </div>
      </details>

      <ErrorText error={error} />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button disabled={!name.trim()} onClick={save}>
          Save rule
        </Button>
      </div>
    </div>
  );
}
